import { yupResolver } from "@hookform/resolvers/yup";
import {
  BuildingApartment,
  EnvelopeSimple,
  Lock,
  User,
  Camera,
  ChartLine,
} from "@phosphor-icons/react";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import PasswordInput from "../components/PasswordInput";
import ProfileSkeleton from "../components/SkeletonLoaders/ProfileSkeleton";
import {
  useAuth,
  useGrowthIndicator,
  useUpdateGrowthIndicator,
} from "../hooks";
import { IMerchant } from "../interfaces";
import { httpService } from "../services/httpService";
import { API_CONFIG, DEFAULT_AVATAR } from "../shared/constants";
import PasswordChangeSkeleton from "../components/SkeletonLoaders/PasswordChangeSkeleton";
import GrowthIndicatorSlider from "../components/GrowthIndicatorSlider";

// interface CurrencyOption {
//   value: string;
//   label: string;
// }

// const currencyOptions: CurrencyOption[] = CURRENCIES_OPTIONS.map(
//   (currency) => ({
//     value: currency.code,
//     label: `${currency.code} - ${currency.name} (${currency.symbol})`,
//   })
// );

interface PasswordChangeForm {
  old_password: string;
  new_password: string;
  new_confirm_password: string;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { user, setUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<Partial<IMerchant>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  // Growth Indicator state and hooks
  const { data: growthIndicatorData, isLoading: isGrowthIndicatorLoading } =
    useGrowthIndicator();
  const updateGrowthIndicator = useUpdateGrowthIndicator();
  const [growthIndicatorRange, setGrowthIndicatorRange] = useState<
    [number, number]
  >([0, 50]);
  const [isGrowthIndicatorSaving, setIsGrowthIndicatorSaving] = useState(false);

  // Check if indicator has been updated by the user
  const isGrowthIndicatorSet =
    growthIndicatorData?.is_growth_indicator_updated ?? false;
  const isTakeOverUser = httpService.getTakeOverUser();

  const schema = yup.object().shape({
    id: yup.number().optional(),
    role: yup.string().optional(),
    is_active: yup.boolean().optional(),
    created_at: yup.string().optional(),
    updated_at: yup.string().optional(),
    is_deleted: yup.boolean().optional(),
    email: yup.string().email().required(),
    schema: yup.string().optional(),
    name: yup.string().nullable().optional(),
    slug: yup.string().optional(),
    is_team_admin: yup.boolean().optional(),
    both_social_account_connected_at: yup.string().nullable().optional(),
    last_sync_at: yup.string().nullable().optional(),
    profile_picture: yup.mixed().optional(),
    company_name: yup.string().nullable().optional(),
    // currency: yup.mixed().nullable().optional(),
    // tax_percentage: yup
    //   .number()
    //   .transform((value, originalValue) =>
    //     originalValue === "" || originalValue === null ? null : value
    //   )
    //   .typeError(t("validation.tax_percentage.number"))
    //   .min(0, t("validation.tax_percentage.min"))
    //   .max(100, t("validation.tax_percentage.max"))
    //   .nullable()
    //   .optional(),
  }) as yup.ObjectSchema<IMerchant>;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IMerchant>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      company_name: "",
      role: "admin",
      // currency: "USD",
      // tax_percentage: 0,
      profile_picture: DEFAULT_AVATAR,
    },
  });

  const passwordSchema = yup.object().shape({
    old_password: yup.string().required(t("validation.required")),
    new_password: yup
      .string()
      .required(t("validation.required"))
      .min(8, t("validation.password.min"))
      .max(64, t("validation.password.max"))
      .notOneOf(
        [yup.ref("old_password")],
        t("validation.password.not_same_as_old")
      )
      .matches(
        /^(?!.*[\p{Emoji_Presentation}\p{Extended_Pictographic}])(?!.*[\p{C}\p{Zl}\p{Zp}])(?=.*[A-Za-z])(?=.*\d)(?!^[^A-Za-z\d]+$)[\s\S]{8,64}$/u,
        t("validation.password.complexity")
      ),
    new_confirm_password: yup
      .string()
      .required(t("validation.required"))
      .oneOf([yup.ref("new_password")], t("validation.password.match")),
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    watch: watchPassword,
  } = useForm<PasswordChangeForm>({
    resolver: yupResolver(passwordSchema),
  });

  const formData = watch();
  const passwordFormData = watchPassword();

  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        if (user) {
          // Store initial values
          setInitialValues({
            name: user.name || "",
            email: user.email || "",
            company_name: user.company_name || "",
            profile_picture: user.profile_picture || DEFAULT_AVATAR,
            // currency: user.currency || "USD",
            // tax_percentage: user.tax_percentage || 0,
          });

          // Set form values using setValue
          Object.entries(user).forEach(([key, value]) => {
            setValue(key as keyof IMerchant, value);
          });
          setValue("profile_picture", user.profile_picture || DEFAULT_AVATAR);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Error loading settings:", error);
      }
    };

    loadSettings();
  }, [setValue, user]);

  // Load growth indicator data
  useEffect(() => {
    if (growthIndicatorData?.growth_indicators) {
      setGrowthIndicatorRange(growthIndicatorData.growth_indicators);
    }
  }, [growthIndicatorData]);

  const hasChanges = () => {
    const currentValues = watch();
    return Object.keys(initialValues).some((key) => {
      const k = key as keyof IMerchant;
      return (currentValues[k] || "") !== (initialValues[k] || "");
    });
  };

  const onSubmit = async (data: IMerchant) => {
    setIsSaving(true);
    try {
      const formDataToSend = new FormData();

      // Only send fields that actually changed
      (Object.keys(initialValues) as (keyof IMerchant)[]).forEach((key) => {
        if (key === "profile_picture") return;
        const currentValue = data[key];
        const originalValue = initialValues[key];
        if ((currentValue ?? "") !== (originalValue ?? "")) {
          formDataToSend.append(key, String(currentValue ?? ""));
        }
      });

      // Handle profile picture
      if (imagePreview && data.profile_picture instanceof File) {
        formDataToSend.append("profile_picture", data.profile_picture);
      }

      formDataToSend.append("is_active", "true");

      const res = await httpService.patch(
        `${API_CONFIG.path.users}/${data.id}`,
        formDataToSend
      );

      setUser(res as IMerchant);
      setInitialValues(data);
      httpService.setUserData(res as IMerchant);
      setIsSaving(false);
      toast.success(t("settings.account.saved"));
    } catch (error) {
      console.error("Error saving settings:", error);
      setIsSaving(false);
      toast.error(t("settings.account.error"));
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB
        setImageError(
          t(
            "settings.account.image_size_error",
            "Image size should not exceed 5MB."
          )
        );
        setValue("profile_picture", null);
        setImagePreview(null);
        return;
      } else {
        setImageError(null);
      }
      setValue("profile_picture", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onPasswordSubmit = async (data: PasswordChangeForm) => {
    setIsPasswordSaving(true);
    try {
      await httpService.patch(API_CONFIG.path.changePassword, data);
      // await axios.patch(API_CONFIG.path.changePassword, data);
      toast.success(t("settings.password.success"));
      // Reset form
      registerPassword("old_password").onChange({ target: { value: "" } });
      registerPassword("new_password").onChange({ target: { value: "" } });
      registerPassword("new_confirm_password").onChange({
        target: { value: "" },
      });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(t("settings.password.error"));
    } finally {
      setIsPasswordSaving(false);
    }
  };

  const onGrowthIndicatorSubmit = async () => {
    setIsGrowthIndicatorSaving(true);
    try {
      await updateGrowthIndicator.mutateAsync({
        indicators: growthIndicatorRange,
      });
      toast.success(t("settings.growthIndicator.success"));
    } catch (error) {
      console.error("Error saving growth indicator:", error);
      toast.error(t("settings.growthIndicator.error"));
    } finally {
      setIsGrowthIndicatorSaving(false);
    }
  };

  return (
    <div className="w-full space-y-4 px-2">
      {/* Profile Settings and Growth Indicator Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profile Settings Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 min-h-[37px] ">
            <User size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
            <h1 className="text-md font-bold text-gray-700 dark:text-gray-300">
              {t("settings.profile.title")}
            </h1>
            <div className="flex-1 h-0.5 bg-gray-200 dark:bg-neutral-700 ml-2 rounded-md" />
          </div>

          {isLoading ? <ProfileSkeleton /> : (
            <>
              {user?.invited_by && (
                <div className="p-4 rounded-lg border border-gray-300 dark:border-neutral-700 flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={
                        (user?.invited_by.profile_picture as string) ||
                        DEFAULT_AVATAR
                      }
                      alt={
                        user?.invited_by.name ||
                        user?.invited_by.email ||
                        "Inviter"
                      }
                      className="w-12 h-12 rounded-full object-cover border border-indigo-200 bg-white dark:bg-neutral-800"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 dark:text-neutral-400 mb-1">Invited by</div>
                    <div className="flex gap-2 items-center">
                      <div className="font-semibold text-gray-700 dark:text-gray-300 text-base flex items-center gap-2">
                        <User size={16} />
                        {user?.invited_by.name || "-"}
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">|</span>
                      <div className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <EnvelopeSimple size={16} />
                        {user?.invited_by.email}
                      </div>
                      {user?.invited_by.company_name && (
                        <>
                          <span className="text-gray-700 dark:text-gray-300">|</span>
                          <div className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                            <BuildingApartment size={16} />
                            {user?.invited_by.company_name || "-"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Profile Picture and Basic Info */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex flex-col justify-center sm:justify-start items-center">
                    <label
                      htmlFor="profilePicInput"
                      className="relative cursor-pointer group h-60 w-60 sm:min-h-[164px] sm:min-w-[164px]"
                    >
                      <img
                        src={
                          imagePreview || (formData.profile_picture as string)
                        }
                        className="rounded-lg border-4 border-gray-300 dark:border-neutral-700 object-cover h-full w-full"
                      />
                      <div className="absolute inset-0 flex items-center rounded-lg justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Camera className="w-8 h-8 text-white" />
                      </div>
                      <input
                        id="profilePicInput"
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                    {imageError && (
                      <div className="text-red-500 text-sm mt-2 ml-4">
                        {imageError}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 flex-1">
                    <div>
                      <div className="mb-2 block">
                        <Label
                          htmlFor="name"
                          value={t("settings.account.name")}
                        />
                      </div>
                      <TextInput
                        id="name"
                        {...register("name")}
                        disabled={isSaving}
                        readOnly={isTakeOverUser}
                        placeholder={t("settings.account.name")}
                        icon={User}
                        color={errors.name ? "failure" : undefined}
                        helperText={errors.name?.message}
                      />
                    </div>
                    <div>
                      <div className="mb-2 block">
                        <Label
                          htmlFor="email"
                          value={t("settings.account.email")}
                        />
                      </div>
                      <TextInput
                        id="email"
                        type="email"
                        disabled={true}
                        value={formData.email}
                        defaultValue={formData.email}
                        readOnly
                        placeholder={t("settings.account.email")}
                        icon={EnvelopeSimple}
                        color={errors.email ? "failure" : undefined}
                        helperText={errors.email?.message}
                      />
                    </div>
                    <div>
                      <div className="mb-2 block">
                        <Label
                          htmlFor="companyName"
                          value={t("settings.account.company")}
                        />
                      </div>
                      <TextInput
                        id="companyName"
                        {...register("company_name")}
                        placeholder={t("settings.account.company")}
                        disabled={isSaving}
                        readOnly={isTakeOverUser}
                        icon={BuildingApartment}
                        color={errors.company_name ? "failure" : undefined}
                        helperText={errors.company_name?.message}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  color="indigo"
                  disabled={isSaving || !hasChanges()}
                  className="w-full"
                  isProcessing={isSaving}
                  processingSpinner={<Spinner color="white" light size="sm" />}
                >
                  {t("settings.account.save")}
                </Button>
              </form>
            </>
          )}
          {/* Password Change Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 min-h-[37px]">
              <Lock size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
              <h1 className="text-md font-bold text-gray-700 dark:text-gray-300">
                {t("settings.password.title")}
              </h1>
              <div className="flex-1 h-0.5 bg-gray-200 dark:bg-neutral-700 ml-2 rounded-md" />
            </div>

            {isLoading ? <PasswordChangeSkeleton /> : (
              <form
                onSubmit={handlePasswordSubmit(onPasswordSubmit)}
                className="space-y-4"
              >
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 block">
                      <Label
                        htmlFor="currentPassword"
                        value={t("settings.password.current")}
                      />
                    </div>
                    <PasswordInput
                      id="currentPassword"
                      placeholder={t("settings.password.current")}
                      {...registerPassword("old_password")}
                      error={passwordErrors.old_password?.message}
                      animationDirection="horizontal"
                    />
                  </div>
                  <div>
                    <div className="mb-2 block">
                      <Label
                        htmlFor="newPassword"
                        value={t("settings.password.new")}
                      />
                    </div>
                    <PasswordInput
                      id="newPassword"
                      placeholder={t("settings.password.new")}
                      {...registerPassword("new_password")}
                      error={passwordErrors.new_password?.message}
                      animationDirection="horizontal"
                    />
                  </div>
                  <div>
                    <div className="mb-2 block">
                      <Label
                        htmlFor="confirmPassword"
                        value={t("settings.password.confirm")}
                      />
                    </div>
                    <PasswordInput
                      id="confirmPassword"
                      placeholder={t("settings.password.confirm")}
                      {...registerPassword("new_confirm_password")}
                      error={passwordErrors.new_confirm_password?.message}
                      animationDirection="horizontal"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  color="indigo"
                  disabled={
                    isPasswordSaving ||
                    !passwordFormData.old_password ||
                    !passwordFormData.new_password ||
                    !passwordFormData.new_confirm_password ||
                    passwordFormData.new_password !==
                    passwordFormData.new_confirm_password
                  }
                  className="w-full"
                  isProcessing={isPasswordSaving}
                  processingSpinner={<Spinner color="white" light size="sm" />}
                >
                  {t("settings.password.change")}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Growth Indicator Settings Section */}
        {user?.is_team_admin && (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2 min-h-[37px]">
                <ChartLine size={20} className="text-gray-700 dark:text-gray-300" weight="duotone" />
                <h1 className="text-md font-bold text-gray-700 dark:text-gray-300">
                  {t("settings.growthIndicator.title")}
                </h1>
                <div className="flex-1 h-0.5 bg-gray-200 dark:bg-neutral-700 ml-2 rounded-md" />
              </div>

              {isLoading || isGrowthIndicatorLoading ? (
                <div className="space-y-4">
                  <div className="h-20 bg-gray-200 dark:bg-neutral-700 animate-pulse rounded-lg" />
                  <div className="h-10 bg-gray-200 dark:bg-neutral-700 animate-pulse rounded-lg" />
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-neutral-400">
                    {t("settings.growthIndicator.description")}
                  </p>

                  {isGrowthIndicatorSet && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-700">
                        <strong>Note:</strong>{" "}
                        {t("settings.growthIndicator.readOnlyNote")}
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-gray-50 dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700">
                    <GrowthIndicatorSlider
                      value={growthIndicatorRange}
                      onChange={setGrowthIndicatorRange}
                      disabled={isGrowthIndicatorSet || isGrowthIndicatorSaving}
                    />
                  </div>

                  {!isGrowthIndicatorSet && (
                    <Button
                      onClick={onGrowthIndicatorSubmit}
                      color="indigo"
                      disabled={isGrowthIndicatorSaving}
                      className="w-full"
                      isProcessing={isGrowthIndicatorSaving}
                      processingSpinner={<Spinner color="white" light size="sm" />}
                    >
                      {isGrowthIndicatorSaving
                        ? t("settings.growthIndicator.saving")
                        : t("settings.growthIndicator.save")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>


    </div>
  );
};

export default Settings;
